import React, { FC, useEffect, useState } from "react";
import { Row, Col, Collapse, Badge, Space, Menu, Dropdown, Button, Checkbox, Spin } from "antd";
import PinUnpin from "@pages/member-details/pin-unpin-component";
import CustomTooltip from "@pages/tool-tip";
import "./patient-profile-matrix.scss";
import { castArray, cloneDeep , get} from "lodash";
import { CloseOutlined, DownOutlined } from "@ant-design/icons";
import { usePatientProfileMatrixStore } from "@stores/patientProfileMatrixStore";
import membersService from "@services/members.service";
import memberDetailsService from "@services/memberDetails.service";
import { CanEdit } from '@components/accessLevel'
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import DataNotFound from "@components/noDataFound";
import { NO_DATA_FOUND } from "../../../../constants/constants";

const { Panel } = Collapse;

export const CategoryHeader = ({onFieldChange, selected, datum}: {onFieldChange: (fieldIndex: number, checked: boolean) => void, selected: string[], datum: any}) => {
    const [open, setopen] = useState<boolean>(false)

    const menu = (
        <Menu
            onClick={e => false}
            className='fields-menu'
            itemType=""
        >
            {
                (datum.fields || []).map(
                    (item: any, index: number) => {
                        return (
                            <Menu.Item key={`categoryHeader${index}`} className={
                                [
                                    item.type === 'patientData' ? 'patientData-item' : '',
                                    selected.includes(item.name) ? 'selected' : 'unselected'
                                ].join(' ')
                            }>
                                <Space>
                                    <Checkbox
                                        checked={selected.includes(item.name)}
                                        onChange={e =>  onFieldChange(index, e.target.checked)}
                                        disabled={item.type === 'patientData'}
                                    />
                                    <span className="label">{item.name}</span>
                                </Space>
                            </Menu.Item>
                        )
                    }
                )
            }
        </Menu>

    );

    return (
        <Row >
            <Col>
                <Space>
                  <CanEdit hide={false}>
                    <div onClick={e => e.stopPropagation()}>
                        <Dropdown
                            overlay={menu}
                            visible={open}
                            onVisibleChange={setopen}
                            className="field-dropdown"
                            overlayClassName="field-dropdown-overlay"
                            trigger={['click']}
                        >
                            <Button>
                                <Space>
                                    <span className="title">
                                        {datum.category}
                                    </span>
                                    <DownOutlined />
                                </Space>
                            </Button>
                        </Dropdown>
                    </div>
                    </CanEdit>

                    <span className="selected">
                        {selected.length}/{(datum.fields || []).length} Selected
                    </span>
                </Space>
            </Col>
        </Row>
    )
}

const PatientProfileMatrix: FC<any> = (props) => {

   const dataCardId = "36";
    const [activeCardPanel, setActiveCardPanel] = useState<any>(props?.activeKey?.includes(dataCardId) ? ["1"] : [])
    const [expanded, setExpanded] = useState<string[]>([])
    const [patientProfileMatrixData, setPatientProfileMatrixData, clearStore] = usePatientProfileMatrixStore(state => [state.patientProfileMatrixData, state.setPatientProfileMatrixData, state.clearStore])
    const [loading, setLoading] = useState(false);
    const patientId = membersService?.getCurrentMember?.patient_id;

    useEffect(() => {
      if(patientProfileMatrixData) {
        setExpanded(patientProfileMatrixData?.values?.map((datum: any) => datum.category));
      }
    }, [patientProfileMatrixData]);

    useEffect(() => {
      if (patientProfileMatrixData?.patientId !== patientId) {
        clearStore();
      }
    }, [patientId]);

    useEffect(()=> {
      setActiveCardPanel(props?.activeKey?.includes(dataCardId) ? ["1"] : [])
      if(!patientProfileMatrixData && props?.activeKey?.includes(dataCardId)) {
        getPatientProfileMatrixData(["1"]);
      }
    }, [props?.activeKey])

    const onCollapseChange = () => {
        if(activeCardPanel.length > 0){
          props?.setActivePanels(cloneDeep(props?.activeKey)?.filter((item:any) => item !== dataCardId))
        }
        else {
            props?.setActivePanels([...(props?.activeKey ? props?.activeKey : []), dataCardId]);
        };
    }

    const collapseAll = expanded.length > 0

    const genExtra = (id: any) => (
      <Row onClick={(e) => e.stopPropagation()}>
        <Col>
          <Space>
            <Button
              type="link"
              className={`expand-collapse-btn ${collapseAll ? "collapse" : "expand"}`}
              onClick={(e) => {
                collapseAll ? setExpanded([]) : setExpanded(patientProfileMatrixData?.values?.map((datum: any) => datum.category));
              }}
            >
              {collapseAll ? "Collapse All" : "Expand All"}
              <DownOutlined />
            </Button>
            <PinUnpin marginRight={"16px"} dataCardId={id} changeHandler={props?.pinChangeHandler} pinCardIds={props.pinCardIds} />
          </Space>
        </Col>
      </Row>
    );

    const getHeading = (headerValue: any) => (
        <>
            <span>{headerValue}</span>
            <CustomTooltip
                toolTipvalue={headerValue}
                children={
                <span
                    style={{ margin: "8px" }}
                    className="icon icon-info clr-lightGrayColor"
                ></span>
                }
            />
        </>
    );

    const getPatientProfileMatrixData = (panelExpanded:any) => {
        if (panelExpanded?.length > 0) {
          setLoading(true)
            memberDetailsService.getPatientProfileMatrixRecords(patientId).subscribe({
              next: (response) => {
                const data = response?.data?.data?.patientProfileManagementMatrix;
                  setPatientProfileMatrixData(data);
                  setLoading(false);
                },   
              error: (e) => {
                console.log(e);
                setLoading(false);
                setPatientProfileMatrixData(null);
              },
            });
        } else console.log("Data card is available, no API call");
      }; 

    const onChange = (categoryIndex: number, fieldIndex: number, checked: boolean) => {
        const tempMatrixData = cloneDeep(patientProfileMatrixData);
        tempMatrixData.values[categoryIndex].fields[fieldIndex].isSelected = checked;
        setPatientProfileMatrixData(tempMatrixData)

        const category = patientProfileMatrixData?.values?.[categoryIndex]
        const field = category.fields[fieldIndex]

        memberDetailsService.updatePatientProfile(patientId, checked, category.category, field.name).subscribe({
            next: (v) => {
            },
            error: (e) => {
            },
        });
    }

    return (
      <>
        <Collapse className="patient-profile-matrix" accordion activeKey={activeCardPanel} expandIconPosition="right" bordered={false} onChange={onCollapseChange}>
          <Panel
            header={getHeading("Patient Profile And Management Matrix")}
            key="1"
            className="title"
            extra={genExtra(dataCardId)}
            style={{ marginBottom: "8px" }}
          >
            { loading ? (
              <Spin spinning={loading} indicator={<HealthemSpinner size={50} />}></Spin>
            ) : get(patientProfileMatrixData , 'values' , []).length > 0 ? (
              <>
                <Row className="types">
                  <Col>
                    <Badge className="type-name" size="small" color="#B3BAC5" text="As Patient Data" />
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    {patientProfileMatrixData?.values?.map((datum: any, index: any) => {
                      const selected = (datum.fields || []).filter((field: any) => field.isSelected).map((field: any) => field.name);

                      return (
                        <Collapse
                          className="main"
                          expandIconPosition="right"
                          bordered={false}
                          key={index}
                          activeKey={selected.length > 0 ? expanded : []}
                          onChange={(key) => setExpanded(castArray(key))}
                        >
                          <Panel
                            header={<CategoryHeader onFieldChange={(fieldIndex, checked) => onChange(index, fieldIndex, checked)} selected={selected} datum={datum} />}
                            collapsible={selected.length === 0 ? "disabled" : undefined}
                            showArrow={selected.length > 0}
                            key={datum.category}
                          >
                            <Space className="items">
                              {(datum.fields || []).map((field: any, fieldIndex: any) => {
                                return selected.includes(field.name) ? (
                                  <Badge
                                    className={`field-name ${field.type === "patientData" ? "" : "no-dot"}`}
                                    key={fieldIndex}
                                    color={field.type === "patientData" ? "#B3BAC5" : ""}
                                    text={
                                      <Space>
                                        <span>{field.name}</span>
                                        <CanEdit>
                                          {field.type !== "patientData" && (
                                            <Button onClick={(e) => onChange(index, fieldIndex, false)} type="text" className="close-btn">
                                              <CloseOutlined />
                                            </Button>
                                          )}
                                        </CanEdit>
                                      </Space>
                                    }
                                  />
                                ) : (
                                  <></>
                                );
                              })}
                            </Space>
                          </Panel>
                        </Collapse>
                      );
                    })}
                  </Col>
                </Row>
              </>
            ) : (
              <DataNotFound message={NO_DATA_FOUND}/>
            )}
          </Panel>
        </Collapse>
      </>
    );
};

export default PatientProfileMatrix;
